// The native app's custom URL scheme return leg for payment methods that hand off to another
// app (currently MobilePay).
//
// An https return URL cannot bring the user back into the app: MobilePay cannot return to our
// in-app browser, so it opens the OS default browser instead, and iOS does not reliably fire a
// Universal Link from the middle of a redirect chain. A custom scheme is the mechanism Adyen
// recommends for native apps, and it is what the OS actually app-switches on.
//
// The scheme URL is built by our own /app-payment-return interstitial page, not handed to Adyen,
// so Adyen's returnUrl rules (1024 chars, no PII, the adyencheckout:// Android SDK form) do not
// apply to it — those constrain the backend's https returnUrl, which is unchanged.

import { Capacitor } from '@capacitor/core';
import { isDevMode } from './isDevMode';
import { Path } from '../routes/components/path';
import { isNativeVersionAtLeast } from '../useCapacitor';

// Registering a URL scheme means editing Info.plist / AndroidManifest.xml, which cannot ship over
// the Capgo OTA channel. Older native builds have no scheme to open, so they must keep the
// existing behavior rather than be sent to a URL they cannot handle.
export const APP_PAYMENT_RETURN_MINIMUM_NATIVE_VERSION = '1.2.4';

// Must match CFBundleURLSchemes in ios/App/App/Info.plist and ios/App/App Dev-Info.plist, and
// the appScheme manifestPlaceholder of each Android flavor in android/app/build.gradle.
const PROD_SCHEME = 'fi.kotipizza.app';
const DEV_SCHEME = 'fi.kotipizzadev.app';
const RETURN_HOST = 'payment-return';

export type ClientPlatform = 'ios' | 'android' | 'web';

// The payment result as it travels from the Payments API through the interstitial into the app.
// Deliberately the same shape PaymentRedirectHandler already reads off the query string, so the
// deep link can be replayed straight into /tilaus/tulos.
export type AppPaymentReturn = {
  status: string;
  orderId: string | null;
  orderKey: string | null;
  errorCode: string | null;
  isGiftCard: boolean;
};

// Same dev/prod switch used for the hosted-page hostnames in useAppWebViewPayment and
// PaymentRedirect: the dev site is only ever reached from the dev app.
export const getAppReturnScheme = (): string => (isDevMode() ? DEV_SCHEME : PROD_SCHEME);

export const buildAppReturnUrl = (result: AppPaymentReturn): string => {
  const params = new URLSearchParams({ status: result.status });

  if (result.orderId) params.set('orderid', result.orderId);
  if (result.orderKey) params.set('order-key', result.orderKey);
  if (result.errorCode) params.set('errorCode', result.errorCode);
  if (result.isGiftCard) params.set('isGiftCard', '1');

  return `${getAppReturnScheme()}://${RETURN_HOST}?${params.toString()}`;
};

// Returns null for anything that is not one of our payment returns, so the URL falls through to
// the other registered handlers and AppUrlListener's generic https routing.
//
// Both schemes are accepted rather than only the current build's: the running native binary and
// the OTA bundle are versioned separately, and claiming the URL that already reached us is always
// better than dropping a completed payment on the floor.
export const parseAppReturnUrl = (url: string): AppPaymentReturn | null => {
  let parsed: URL;

  try {
    parsed = new URL(url);
  } catch {
    return null;
  }

  const scheme = parsed.protocol.replace(/:$/, '');
  if (scheme !== PROD_SCHEME && scheme !== DEV_SCHEME) return null;
  if (parsed.hostname !== RETURN_HOST) return null;

  const status = parsed.searchParams.get('status');
  if (!status) return null;

  return {
    status,
    orderId: parsed.searchParams.get('orderid'),
    orderKey: parsed.searchParams.get('order-key'),
    errorCode: parsed.searchParams.get('errorCode'),
    isGiftCard: parsed.searchParams.get('isGiftCard') === '1',
  };
};

export const isAppPaymentReturnSupported = (isNative: boolean): boolean =>
  isNative && isNativeVersionAtLeast(APP_PAYMENT_RETURN_MINIMUM_NATIVE_VERSION);

// What the Payments API is told to build the return path for. 'web' keeps today's behavior, so a
// native build that cannot open the scheme yet is indistinguishable from the browser.
export const getPaymentClientPlatform = (): ClientPlatform => {
  if (!isAppPaymentReturnSupported(Capacitor.isNativePlatform())) return 'web';

  const platform = Capacitor.getPlatform();
  return platform === 'ios' || platform === 'android' ? platform : 'web';
};

// The in-app destination for a return: the same /tilaus/tulos (or gift card) URL the web flow
// lands on, so PaymentRedirectHandler / GiftCardPaymentRedirectHandler handle the result exactly
// as they do on web — including their paymentResultKey idempotency guard, which is what keeps
// purchase analytics firing once when the deep link races the /payment-redirect status poll.
export const buildPaymentResultPath = (result: AppPaymentReturn): string => {
  const params = new URLSearchParams({ status: result.status });

  if (result.orderId) params.set('orderid', result.orderId);
  if (result.orderKey) params.set('order-key', result.orderKey);
  if (result.errorCode) params.set('errorCode', result.errorCode);

  const path = result.isGiftCard ? Path.GiftCardOrderStatus : Path.OrderStatus;

  return `${path}?${params.toString()}`;
};
